// src/components/VerifyEmailBanner.jsx
//
// M2 — email verification, as a SOFT gate.
//
// Requiring `email_verified` in authMiddleware would have locked out every
// existing user on deploy, so verification is enforced only where identity
// actually matters (accepting an invitation, minting a token, changing email).
// Everywhere else it is this: a dismissible nudge.
//
// Dismissal is per-session (sessionStorage, not localStorage) — a permanent
// dismissal would defeat the nudge, but re-nagging on every route change would
// be obnoxious.
import React, { useContext, useEffect, useState } from 'react';
import { UserContext } from '../context/UserContext';
import InlineBanner from './common/InlineBanner';

const DISMISS_KEY = 'qdex_verify_banner_dismissed';

function VerifyEmailBanner() {
    const { user, emailVerified, resendVerification } = useContext(UserContext);

    const [dismissed, setDismissed] = useState(() => {
        try {
            return sessionStorage.getItem(DISMISS_KEY) === '1';
        } catch {
            return false;
        }
    });
    const [state, setState] = useState('idle'); // idle | sending | sent | error

    // A user who verifies in another tab should stop seeing this without a reload.
    useEffect(() => {
        if (emailVerified) setState('idle');
    }, [emailVerified]);

    if (!user || emailVerified || dismissed) return null;

    const handleResend = async () => {
        setState('sending');
        try {
            await resendVerification();
            setState('sent');
        } catch (err) {
            console.error('Could not send verification email:', err);
            setState('error');
        }
    };

    const dismiss = () => {
        setDismissed(true);
        try {
            sessionStorage.setItem(DISMISS_KEY, '1');
        } catch {
            /* Private-mode storage failure shouldn't break dismissal for this view. */
        }
    };

    return (
        <InlineBanner
            className="qd-verify-banner"
            variant={state === 'error' ? 'danger' : 'warning'}
            onDismiss={dismiss}
        >
            {state === 'sent' ? (
                <>Verification email sent to <strong>{user.email}</strong>. Check your inbox and spam folder.</>
            ) : state === 'error' ? (
                <>Couldn't send the verification email just now. Please try again in a moment.</>
            ) : (
                <>
                    Confirm your email address to be invited to workspaces and connect desktop apps.
                    {' '}
                    <button
                        type="button"
                        className="qd-btn qd-btn--sm"
                        onClick={handleResend}
                        disabled={state === 'sending'}
                    >
                        {state === 'sending' ? 'Sending…' : 'Send confirmation email'}
                    </button>
                </>
            )}
        </InlineBanner>
    );
}

export default VerifyEmailBanner;
